import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';
import LoadingScreen from '../components/LoadingScreen';

export default function CorporateEventPublic() {
  const { shareLink } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('info');
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', organization: '', role: '', interests: '', dietary: '', message: '' });
  const [registering, setRegistering] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [regError, setRegError] = useState('');
  const [voteSent, setVoteSent] = useState(false);
  const [question, setQuestion] = useState('');
  const [qSending, setQSending] = useState(false);

  useEffect(() => {
    loadEvent();
    // eslint-disable-next-line
  }, [shareLink]);

  async function loadEvent() {
    try {
      const res = await api.get(`/corporate-event/${shareLink}`);
      setEvent(res.data);
    } catch {
      setError('Event not found or link may be expired.');
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister(e) {
    e.preventDefault();
    setRegistering(true);
    setRegError('');
    try {
      await api.post(`/corporate-event/${shareLink}/register`, form);
      setRegistered(true);
    } catch (err) {
      setRegError(err.response?.data?.error || 'Registration failed');
    } finally {
      setRegistering(false);
    }
  }

  async function handleVote(option) {
    try {
      await api.post(`/corporate-event/${shareLink}/poll-vote`, { option });
      setVoteSent(true);
      loadEvent();
    } catch {
      // vote failed
    }
  }

  async function handleSubmitQuestion(e) {
    e.preventDefault();
    if (!question.trim()) return;
    setQSending(true);
    try {
      await api.post(`/corporate-event/${shareLink}/question`, { question });
      setQuestion('');
      loadEvent();
    } catch {
      alert('Failed to submit question');
    } finally {
      setQSending(false);
    }
  }

  if (loading) return <LoadingScreen />;
  if (error) return <div className="min-h-screen flex items-center justify-center text-red-600">{error}</div>;
  if (!event) return null;

  const totalVotes = event.poll?.votes ? Object.values(event.poll.votes).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      {event.cover_image && (
        <div className="h-64 bg-gray-200 overflow-hidden">
          <img src={event.cover_image} alt={event.title} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="bg-gradient-to-r from-purple-700 to-indigo-800 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{event.title}</h1>
          <p className="text-lg opacity-90">{event.organizer}</p>
          <div className="flex flex-wrap gap-4 mt-3 text-sm">
            <span>📅 {event.event_date}</span>
            {event.event_time_str && <span>🕐 {event.event_time_str}</span>}
            {event.location && <span>📍 {event.location}</span>}
            <span>👥 {event.registered_count} registered</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 mt-6">
        <div className="flex border-b mb-6 overflow-x-auto">
          {['info', 'agenda', 'speakers', 'sponsors', 'poll', 'qa', 'register'].map(t => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 ${tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Info Tab */}
        {tab === 'info' && (
          <div className="space-y-4">
            {event.description && <p className="text-gray-700 whitespace-pre-wrap">{event.description}</p>}
            {event.materials?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2">Downloads</h3>
                <div className="space-y-1">
                  {event.materials.map(m => (
                    <a key={m.id} href={m.file_url} target="_blank" rel="noreferrer" className="block text-blue-600 hover:underline">{m.title}</a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Agenda Tab */}
        {tab === 'agenda' && (
          <div>
            {event.agenda?.length > 0 ? (
              <div className="space-y-3">
                {event.agenda.map(item => (
                  <div key={item.id} className={`p-4 rounded border ${item.is_keynote ? 'border-yellow-400 bg-yellow-50' : item.is_sponsor ? 'border-gray-300 bg-gray-50' : 'border-gray-200'}`}>
                    <div className="flex items-start gap-3">
                      {item.time_str && <span className="text-sm font-mono text-gray-500 mt-0.5">{item.time_str}</span>}
                      <div>
                        <p className="font-medium">{item.title}</p>
                        {item.speaker && <p className="text-sm text-gray-600">{item.speaker}</p>}
                        {item.track_name && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{item.track_name}</span>}
                        {item.description && <p className="text-sm text-gray-600 mt-1">{item.description}</p>}
                        {item.is_keynote && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded ml-2">Keynote</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-gray-500">Agenda not yet available.</p>}
          </div>
        )}

        {/* Speakers Tab */}
        {tab === 'speakers' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {event.speakers?.length > 0 ? event.speakers.map((s, i) => (
              <div key={i} className="bg-white p-4 rounded shadow">
                {s.name && <h4 className="font-semibold">{s.name}</h4>}
                {s.title && <p className="text-sm text-gray-600">{s.title}</p>}
                {s.bio && <p className="text-sm text-gray-700 mt-1">{s.bio}</p>}
              </div>
            )) : <p className="text-gray-500 col-span-2">Speaker list not available.</p>}
          </div>
        )}

        {/* Sponsors Tab */}
        {tab === 'sponsors' && (
          <div className="flex flex-wrap gap-3">
            {event.sponsors?.length > 0 ? event.sponsors.map((s, i) => (
              <div key={i} className="bg-white px-4 py-2 rounded shadow text-sm">{s}</div>
            )) : <p className="text-gray-500">Sponsors to be announced.</p>}
          </div>
        )}

        {/* Poll Tab */}
        {tab === 'poll' && (
          <div>
            {event.poll ? (
              <div className="bg-white p-5 rounded shadow">
                <h3 className="font-semibold text-lg mb-4">{event.poll.question}</h3>
                {Object.entries(event.poll.options || {}).map(([option, count]) => (
                  <div key={option} className="mb-3">
                    <button
                      onClick={() => !voteSent && handleVote(option)}
                      disabled={voteSent}
                      className="w-full text-left px-3 py-2 border rounded hover:bg-blue-50 disabled:cursor-default disabled:hover:bg-white"
                    >
                      <span>{option}</span>
                      <span className="float-right text-sm text-gray-500">{totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0}%</span>
                    </button>
                    <div className="h-2 bg-gray-200 rounded mt-1">
                      <div className="h-2 bg-blue-500 rounded transition-all" style={{ width: `${totalVotes > 0 ? (count / totalVotes) * 100 : 0}%` }} />
                    </div>
                  </div>
                ))}
                {voteSent && <p className="text-sm text-green-600 mt-2">✓ Vote recorded!</p>}
              </div>
            ) : <p className="text-gray-500">No active poll.</p>}
          </div>
        )}

        {/* Q&A Tab */}
        {tab === 'qa' && (
          <div className="space-y-4">
            <form onSubmit={handleSubmitQuestion} className="flex gap-2">
              <input value={question} onChange={e => setQuestion(e.target.value)} placeholder="Ask a question..." className="flex-1 border p-2 rounded" disabled={qSending} />
              <button type="submit" disabled={qSending || !question.trim()} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50">
                {qSending ? '...' : 'Submit'}
              </button>
            </form>
            {event.questions?.length > 0 ? event.questions.map(q => (
              <div key={q.id} className="bg-white p-4 rounded shadow">
                <p className="text-gray-800">{q.question}</p>
                <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                  <span>👍 {q.likes || 0}</span>
                  {q.answered && q.answer && <span className="text-green-600">✓ {q.answer}</span>}
                </div>
              </div>
            )) : <p className="text-gray-500">No questions yet. Be the first to ask!</p>}
          </div>
        )}

        {/* Register Tab */}
        {tab === 'register' && (
          <div>
            {registered ? (
              <div className="bg-green-50 border border-green-200 p-6 rounded text-center">
                <h3 className="text-green-700 font-semibold text-lg mb-2">✓ You're registered!</h3>
                <p className="text-green-600">We'll send details to {form.email}. See you there!</p>
              </div>
            ) : (
              <form onSubmit={handleRegister} className="bg-white p-6 rounded shadow space-y-4">
                <h3 className="font-semibold text-lg">Register to Attend</h3>
                {regError && <p className="text-red-600 text-sm">{regError}</p>}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input required placeholder="Full Name *" className="border p-2 rounded w-full" value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} />
                  <input required type="email" placeholder="Email *" className="border p-2 rounded w-full" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                  <input placeholder="Phone" className="border p-2 rounded w-full" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                  <input placeholder="Organization / Company" className="border p-2 rounded w-full" value={form.organization} onChange={e => setForm({ ...form, organization: e.target.value })} />
                  <input placeholder="Your Role" className="border p-2 rounded w-full" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} />
                  <input placeholder="Areas of Interest" className="border p-2 rounded w-full" value={form.interests} onChange={e => setForm({ ...form, interests: e.target.value })} />
                  <input placeholder="Dietary Requirements" className="border p-2 rounded w-full" value={form.dietary} onChange={e => setForm({ ...form, dietary: e.target.value })} />
                </div>
                <textarea placeholder="Message (optional)" className="border p-2 rounded w-full" rows={2} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} />
                <button type="submit" disabled={registering} className="w-full bg-purple-700 text-white py-3 rounded font-semibold hover:bg-purple-800 disabled:opacity-50">
                  {registering ? 'Registering...' : 'Register Now'}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
